/**
 * The livescore diffusion adapter handles
 * connection & relaying messages from Diffusion.
 * Handles connection, subscription, unsubscription.
 *
 */
import Router from '../../controllers/Router';
import MessageBusTopics from '../../../common/MessageBusTopics';
import { subscribe, unsubscribe, unsubscribeAll } from '@push-component/client';
import { LIVE_SCORE } from '@push-component/constants';

const OWNER = 'sportsbook-web-livescores';

/**
 * Livescores config passed via deploy-time wsc config.
 *
 * @type {Object}
 *
 * @private
 */
const conf = window.whStatic.config.livescore || {};

/**
 * Global PubSub reference
 *
 * @type {Object}
 *
 * @private
 */
const pubSub = window.WH.messageBus;

/**
 * Subscribe to various msgs.
 *
 * @returns {void}
 *
 * @private
 */
const bindPubSubMessages = () => {
    pubSub.subscribe(MessageBusTopics.LIVESCORE_SUBSCRIBE, handlePubSubSubscription);
    pubSub.subscribe(MessageBusTopics.LIVESCORE_UNSUBSCRIBE, handlePubSubUnsubscription);
};

/**
 * Handles pubSub subscription
 *
 * @param {String}   topic  The pubsub topic
 * @param {Object} payload  Subscription payload
 *
 * @returns {void}
 *
 * @private
 */
const handlePubSubSubscription = (topic, { sport, eventId }) => {
    api.subscribe(sport, eventId, (message) => {
        pubSub.publish(MessageBusTopics.LIVESCORE_MESSAGE, { sport, eventId, message });
    });
};

/**
 * TODO: clarify if api.unsubscribe requires callback or not.
 *
 * Handles pubSub unsubscription
 *
 * @param {String}   topic  The pubsub topic
 * @param {Object} payload  Subscription payload
 *
 * @returns {void}
 *
 * @private
 */
const handlePubSubUnsubscription = (topic, { eventId }) => {
    api.unsubscribe(eventId);
};

/**
 * Initialise module.
 *
 * @return {void}
 *
 * @private
 */
const init = () => {
    if (!conf.enabled) {
        return;
    }

    bindPubSubMessages();
    Router.hooks.add(api.unsubscribeAll);  // reset on url change;
};

/**
 * Pad a string.
 *
 * @param {String} string String to pad.
 * @param {String} pad Padding.
 * @param {Integer} length How much to pad left.
 * @returns {String}
 * @public
 */
const pad = (string, pad, length) => {

    const result = string.toString();
    if (result.length >= length) {
        return result;
    }
    return (new Array(length + 1).join(pad) + string).slice(-length);
};

/**
 * Format a game time for display.
 * e.g. 4351 => 72:31
 *
 * @param {Integer} time Game time in seconds.
 * @returns {String} Formatted game time for display.
 * @public
 */
const formatTime = time => {

    const mins = pad(Math.floor(time / 60), '0', 2);
    const secs = pad(time % 60, '0', 2);

    return mins + ':' + secs;
};

/**
 * Footy summary topic message handler.
 *
 * @param  {Object} json       event state summary json.
 *
 * @return {Object} event data enriched both with score & time.
 *
 * @private
 */
const fbProcessor = (json) => {
    return {
        set: formatTime(json.clock.currentTime.duration),
        leg: `${json.score.home} - ${json.score.away}`,
    };
};

const gameScoreProcessor = (json) => {
    if (json && json.scores && json.scores.setScore && json.scores.gameScore) {
        return {
            set: `${json.scores.gameScore.home} - ${json.scores.gameScore.away}`,
            leg: `${json.scores.setScore.home} - ${json.scores.setScore.away}`,
        };
    }
};

/**
 * volleyball summary topic message handler.
 *
 * @param  {Object} json       event state summary json.
 *
 * @return {Object} event data enriched both with score & time.
 *
 * @private
 */
const volleyballProcessor = (json) => {
    if (json.score && json.setScore) {
        return {
            set: `${json.score.team1} - ${json.score.team2}`,
            leg: `${json.setScore.team1} - ${json.setScore.team2}`,
        };
    }

    return gameScoreProcessor(json);
};

/**
 * tennis summary topic message handler.
 *
 * @param  {Object} json       event state summary json.
 *
 * @return {Object} event data enriched both with score & time.
 *
 * @private
 */
const tennisProcessor = (json) => {
    if (json.score.match && json.score.currentSet) {
        return {
            set: `${json.score.match.p1} - ${json.score.match.p2}`,
            leg: `${json.score.currentSet.p1} - ${json.score.currentSet.p2}`,
        };
    }
};

/**
 * table-tennis summary topic message handler.
 *
 * @param  {Object} json       event state summary json.
 *
 * @return {Object} event data enriched both with score & time.
 *
 * @private
 */
const tableTennisProcessor = (json) => {
    if (json.score && json.score.match && json.score.currentGame) {
        return {
            set: `${json.score.match.player1} - ${json.score.match.player2}`,
            leg: `${json.score.currentGame.player1} - ${json.score.currentGame.player2}`,
        };
    }

    return gameScoreProcessor(json);
};

const bbPeriodMap = {
    'HALF_1': '1st ',
    'HALF_2': '2nd ',
    'HALF_TIME': 'HT ',
    'QUARTER_1': '1st ',
    'QUARTER_2': '2nd ',
    'QUARTER_3': '3rd ',
    'QUARTER_4': '4th ',
    'OVERTIME': 'OT ',
};

/**
 * Returns the translated Basketball period.
 *
 * @param  {string} period provided in eventData
 *
 * @return {string} period name or empty string
 *
 * @private
 */
const translateBasketballPeriod = (period) => (period in bbPeriodMap ? bbPeriodMap[period] : '');

/**
 * Basketball summary topic message handler.
 *
 * @param  {Object} json       event state summary json.
 *
 * @return {Object} event data enriched both with scores & time.
 *
 * @private
 */
const basketballProcessor = (json) => {
    return {
        period: translateBasketballPeriod(json.clock.currentTime.period),
        set: formatTime(json.clock.currentTime.duration),
        leg: json.us === true ?
            `${json.score.away} - ${json.score.home}` :
            `${json.score.home} - ${json.score.away}`,
    };
};

/**
 * Maps sport to its corresponding processor that'll
 * be used to process livescore messages received from diffusion.
 *
 * Note this needs to contain logic for _all_ the sports than can be
 * whitelisted by livescore configuration.
 *
 * @param  {String} sport
 *
 * @return {Function} logic to process given sport's message with.
 *
 * @private
 */
const sportToMsgProcessor = sport => {
    switch (sport) {
    case 'football':
        return fbProcessor;
    case 'basketball':
        return basketballProcessor;
    case 'tennis':
        return tennisProcessor;
    case 'volleyball':
        return volleyballProcessor;
    case 'table-tennis':
        return tableTennisProcessor;
    default:
        return null;
    }
};

/**
 * Public API for this component
 *
 * @type {Object}
 *
 * @public
 */
const api = {

    /**
     * Subscribe action - Subscribes to Push topic based on
     * data received (sport, eventId).
     *
     * @param {String}      sport  sport name slug.
     * @param {String}    eventId  event id.
     * @param {Function} callback  subscriber callback to call with processed
     *                             livescore data.
     *
     * @return {Boolean} true, if subscription was done and successful; note you'd
     * get false in cases such as asked sport doesn't support livescores, or has
     * been configured off.
     *
     * @public
     */
    subscribe: (sport, eventId, callback) => {
        if (!conf.enabled || typeof callback !== 'function' || conf.whitelist.indexOf(sport) === -1) return false;

        const processor = sportToMsgProcessor(sport);

        subscribe({
            type: LIVE_SCORE,
            owner: OWNER,
            eventId,
            callback: ({ data }) => callback(data ? { eventId, ...processor(data) } : { eventId }),
        });

        return true;
    },

    /**
     * Unsubscribe from livescore service for given eventId (and optionally for given callback).
     * Note if no callback is given, then _all_ subscriptions are removed for this event.
     *
     * @param {String}    eventId  event id to unsubscribe from livescores for.
     *
     * @return {void}
     *
     * @public
     */
    unsubscribe: (eventId) => {

        unsubscribe({
            type: LIVE_SCORE,
            owner: OWNER,
            eventId,
        });

    },

    /**
     * Unsubscribe from all topics.
     *
     * @return {void}
     *
     * @public
     */
    unsubscribeAll: () => {

        unsubscribeAll({
            type: LIVE_SCORE,
            owner: OWNER,
        });

    },
};

init();

export default api;
